(() => {
  'use strict';

  if (window.__G21_STABLE_PRODUCT_NAV__) return;
  window.__G21_STABLE_PRODUCT_NAV__ = true;

  const CACHE_TTL = 30 * 60 * 1000;
  const FETCH_TIMEOUT = 6000;
  const COLLECTION_PAGE_SIZE = 250;
  const SEARCH_PAGE_SIZE = 50;
  const MAX_PAGE_REQUESTS = 100;
  const SESSION_COLLECTION = 'g21:last-product-collection';
  const VIDEO_SELECTOR = 'media-gallery .product-media video';
  const NAVIGATION_HOST_SELECTOR = '.g21-product-navigation-host';
  const navigationLayoutFrames = new WeakMap();
  const navigationLayoutObservers = new Map();

  const safeSessionGet = (key) => {
    try {
      return sessionStorage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  };

  const safeSessionSet = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
  };

  const decodeHandle = (value) => {
    try {
      return decodeURIComponent(value || '').trim();
    } catch (_) {
      return String(value || '').trim();
    }
  };

  const productHandleFromPath = (pathname = location.pathname) => {
    const match = pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeHandle(match[1]) : '';
  };

  const collectionFromPath = (pathname = location.pathname) => {
    const match = pathname.match(/\/collections\/([^/?#]+)\/products\//i);
    return match ? decodeHandle(match[1]) : '';
  };

  const collectionFromAnyPath = (pathname = '') => {
    const match = pathname.match(/\/collections\/([^/?#]+)/i);
    return match ? decodeHandle(match[1]) : '';
  };

  const collectionFromReferrer = () => {
    if (!document.referrer) return '';
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === location.origin ? collectionFromAnyPath(referrer.pathname) : '';
    } catch (_) {
      return '';
    }
  };

  const readData = (scope) => {
    const node = scope.querySelector?.('[data-g21-product-navigation-data]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return {};
    }
  };

  const resolveCollection = (scope, data) => {
    const nav = scope.querySelector?.('[data-g21-product-navigation]');
    const params = new URLSearchParams(location.search);
    const handle = [
      collectionFromPath(),
      params.get('collection'),
      data.collectionHandle,
      nav?.dataset.collectionHandle,
      collectionFromReferrer(),
      safeSessionGet(SESSION_COLLECTION),
      'all'
    ].map((value) => String(value || '').trim()).find(Boolean) || 'all';

    safeSessionSet(SESSION_COLLECTION, handle);
    return handle;
  };

  const localeRoot = () => {
    const root = String(window.Shopify?.routes?.root || '/');
    return root.startsWith('/') ? root : `/${root}`;
  };

  const productPath = (collectionHandle, productHandle) => {
    const root = localeRoot();
    return `${root}collections/${encodeURIComponent(collectionHandle)}/products/${encodeURIComponent(productHandle)}`
      .replace(/\/+/g, '/');
  };

  const keepContext = (href, collectionHandle) => {
    if (!href) return '';
    const target = new URL(href, location.origin);
    const source = new URL(location.href);

    source.searchParams.forEach((value, key) => {
      if (!target.searchParams.has(key)) target.searchParams.append(key, value);
    });

    if (collectionHandle) {
      target.searchParams.set('collection', collectionHandle);
      const handle = productHandleFromPath(target.pathname);
      if (handle) target.pathname = productPath(collectionHandle, handle);
    }

    return `${target.pathname}${target.search}${target.hash}`;
  };

  const codeParts = (product) => {
    const source = `${product.title || ''} ${product.handle || ''}`.toUpperCase();
    const match = source.match(/\bG21[\s_-]?(\d+)([A-Z]*)\b/i);
    if (!match) return null;
    return {
      number: Number.parseInt(match[1], 10),
      suffix: (match[2] || '').toUpperCase()
    };
  };

  const orderedProducts = (products) => {
    const seenHandles = new Set();
    const entries = products
      .filter((product) => {
        if (!product?.handle || seenHandles.has(product.handle)) return false;
        seenHandles.add(product.handle);
        return true;
      })
      .map((product, index) => ({ product, index, code: codeParts(product) }));

    if (!entries.length || entries.some((entry) => !entry.code)) {
      return entries.map((entry) => entry.product);
    }

    return entries
      .sort((a, b) => (
        a.code.number - b.code.number ||
        a.code.suffix.localeCompare(b.code.suffix, undefined, { numeric: true, sensitivity: 'base' }) ||
        a.index - b.index
      ))
      .map((entry) => entry.product);
  };

  const cacheKey = (handle, sortBy) => (
    `g21:product-order:v6:${document.documentElement.lang || 'default'}:${handle}:${sortBy || 'default'}`
  );

  const readCache = (handle, sortBy, expectedCount) => {
    try {
      const cached = JSON.parse(safeSessionGet(cacheKey(handle, sortBy)) || 'null');
      if (
        !cached ||
        cached.complete !== true ||
        !Array.isArray(cached.products) ||
        Date.now() - Number(cached.savedAt || 0) > CACHE_TTL ||
        (expectedCount > 0 && cached.products.length < expectedCount)
      ) return null;
      return cached.products;
    } catch (_) {
      return null;
    }
  };

  const writeCache = (handle, sortBy, products, expectedCount) => {
    safeSessionSet(cacheKey(handle, sortBy), JSON.stringify({
      savedAt: Date.now(),
      expectedCount,
      complete: true,
      products
    }));
  };

  const fetchProductsOnce = async (handle, expectedCount = 0) => {
    const sortBy = new URLSearchParams(location.search).get('sort_by') || '';
    const cached = readCache(handle, sortBy, expectedCount);
    if (cached) return cached;

    const lightweightPath = handle === 'all'
      ? `${localeRoot()}search`
      : `${localeRoot()}collections/${encodeURIComponent(handle)}`;
    const lightweightEndpoint = new URL(lightweightPath, location.origin);
    lightweightEndpoint.searchParams.set('view', 'g21-navigation');
    if (handle === 'all') {
      lightweightEndpoint.searchParams.set('q', 'G21');
      lightweightEndpoint.searchParams.set('type', 'product');
    }
    if (sortBy) lightweightEndpoint.searchParams.set('sort_by', sortBy);

    const ajaxPath = handle === 'all'
      ? `${localeRoot()}products.json`
      : `${localeRoot()}collections/${encodeURIComponent(handle)}/products.json`;
    const ajaxEndpoint = new URL(ajaxPath, location.origin);
    if (sortBy) ajaxEndpoint.searchParams.set('sort_by', sortBy);

    const requestPayload = async (endpoint, allowEmpty = false) => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT);
      try {
        const response = await fetch(endpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!Array.isArray(payload.products) || (!allowEmpty && !payload.products.length)) {
          throw new Error('Empty collection response');
        }
        return payload;
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    const fetchAllPages = async (baseEndpoint, pageSize) => {
      const products = [];
      const seenHandles = new Set();
      let declaredPages = 0;

      for (let page = 1; page <= MAX_PAGE_REQUESTS; page += 1) {
        const endpoint = new URL(baseEndpoint);
        endpoint.searchParams.set('page', String(page));
        if (endpoint.pathname.endsWith('/products.json')) {
          endpoint.searchParams.set('limit', String(pageSize));
        }

        const payload = await requestPayload(endpoint, page > 1);
        const pageProducts = payload.products;
        let added = 0;

        pageProducts.forEach((product) => {
          const productHandle = product?.handle;
          if (!productHandle || seenHandles.has(productHandle)) return;
          seenHandles.add(productHandle);
          const imageCandidate = product.image ||
            product.featured_image ||
            product.images?.[0]?.src ||
            product.images?.[0] ||
            '';
          products.push({
            handle: productHandle,
            title: product.title || '',
            image: typeof imageCandidate === 'string' ? imageCandidate : imageCandidate?.src || '',
            imageAlt: product.imageAlt || product.title || ''
          });
          added += 1;
        });

        declaredPages = Math.max(declaredPages, Number(payload.pages) || 0);
        if (expectedCount > 0 && products.length >= expectedCount) break;
        if (declaredPages > 0 && page >= declaredPages) break;
        if (pageProducts.length < pageSize || added === 0) break;
      }

      return products;
    };

    const lightweightPageSize = handle === 'all' ? SEARCH_PAGE_SIZE : COLLECTION_PAGE_SIZE;
    let products = [];

    try {
      products = await fetchAllPages(lightweightEndpoint, lightweightPageSize);
    } catch (_) {}

    if (expectedCount > 0 && products.length < expectedCount) {
      try {
        const ajaxProducts = await fetchAllPages(ajaxEndpoint, COLLECTION_PAGE_SIZE);
        if (ajaxProducts.length > products.length) products = ajaxProducts;
      } catch (_) {}
    }

    if (!products.length || (expectedCount > 0 && products.length < expectedCount)) {
      throw new Error('Incomplete collection response');
    }

    writeCache(handle, sortBy, products, expectedCount);
    return products;
  };

  const setThumbnail = (link, product) => {
    if (!link || !product?.image) return false;

    let image = link.querySelector('[data-g21-navigation-image]');
    if (!(image instanceof HTMLImageElement)) {
      image = document.createElement('img');
      image.className = 'g21-product-navigation__image';
      image.dataset.g21NavigationImage = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      link.prepend(image);
    }

    const markReady = () => {
      link.classList.add('g21-product-navigation__link--ready');
      scheduleNavigationLayout(link.closest(NAVIGATION_HOST_SELECTOR));
    };
    const markFailed = () => {
      link.classList.remove('g21-product-navigation__link--ready');
      link.hidden = true;
    };

    link.classList.remove('g21-product-navigation__link--ready');
    image.addEventListener('load', markReady, { once: true });
    image.addEventListener('error', markFailed, { once: true });
    image.alt = product.imageAlt || product.title || '';
    image.src = product.image;
    if (image.complete && image.naturalWidth > 0) markReady();
    return true;
  };

  const setLink = (link, product, collectionHandle) => {
    if (!link || !product?.handle || !setThumbnail(link, product)) return false;
    link.href = keepContext(productPath(collectionHandle, product.handle), collectionHandle);
    const label = link.dataset.g21Label || '';
    if (label) link.setAttribute('aria-label', product.title ? `${label}: ${product.title}` : label);
    link.hidden = false;
    link.removeAttribute('aria-disabled');
    link.style.removeProperty('pointer-events');
    return true;
  };

  const activateServerLinks = (nav, collectionHandle) => {
    let count = 0;
    nav.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      if (!link.querySelector('[data-g21-navigation-image]')) return;
      link.href = keepContext(href, collectionHandle);
      link.hidden = false;
      link.removeAttribute('aria-disabled');
      count += 1;
    });
    return count;
  };

  const installClickGuard = (nav) => {
    if (nav.dataset.g21ClickGuard === 'true') return;
    nav.dataset.g21ClickGuard = 'true';
    nav.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !nav.contains(link)) return;
      if (nav.dataset.g21Navigating === 'true') {
        event.preventDefault();
        return;
      }
      nav.dataset.g21Navigating = 'true';
      nav.setAttribute('aria-busy', 'true');
      window.setTimeout(() => {
        delete nav.dataset.g21Navigating;
        nav.removeAttribute('aria-busy');
      }, 5000);
    });
  };

  const useSelfFallback = (nav, collectionHandle) => {
    const current = productHandleFromPath();
    if (!current) return false;
    const existingImage = nav.querySelector('[data-g21-navigation-image]')?.currentSrc ||
      nav.querySelector('[data-g21-navigation-image]')?.src ||
      '';
    if (!existingImage) return false;
    const currentProduct = {
      handle: current,
      title: document.querySelector('h1')?.textContent?.trim() || document.title,
      image: existingImage,
      imageAlt: document.querySelector('h1')?.textContent?.trim() || ''
    };
    const previousReady = setLink(nav.querySelector('[data-g21-product-previous]'), currentProduct, collectionHandle);
    const nextReady = setLink(nav.querySelector('[data-g21-product-next]'), currentProduct, collectionHandle);
    return previousReady && nextReady;
  };

  const containedMediaRect = (element) => {
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const style = getComputedStyle(element);
    if (style.objectFit !== 'contain') return rect;

    const intrinsicWidth = element instanceof HTMLVideoElement
      ? element.videoWidth
      : element instanceof HTMLImageElement
        ? element.naturalWidth
        : 0;
    const intrinsicHeight = element instanceof HTMLVideoElement
      ? element.videoHeight
      : element instanceof HTMLImageElement
        ? element.naturalHeight
        : 0;
    if (!intrinsicWidth || !intrinsicHeight) return rect;

    const scale = Math.min(rect.width / intrinsicWidth, rect.height / intrinsicHeight);
    const width = intrinsicWidth * scale;
    const height = intrinsicHeight * scale;
    const left = rect.left + (rect.width - width) / 2;
    const top = rect.top + (rect.height - height) / 2;

    return {
      left,
      top,
      right: left + width,
      bottom: top + height,
      width,
      height
    };
  };

  const activeProductMedia = (host) => {
    const gallery = host?.querySelector(':scope > media-gallery');
    if (!gallery) return null;
    const activeSlide = gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
      gallery.querySelector('.media-gallery__grid .product-media-container');
    if (!activeSlide) return null;
    return activeSlide.querySelector(
      '.product-media > video, .product-media > img, .product-media__image, model-viewer, iframe'
    ) || activeSlide;
  };

  const updateNavigationLayout = (host) => {
    if (!host?.isConnected) return;
    const nav = host.querySelector('[data-g21-product-navigation]');
    const media = activeProductMedia(host);
    const hostRect = host.getBoundingClientRect();
    const mediaRect = containedMediaRect(media);
    if (!nav || !hostRect.width || !mediaRect) return;

    const mediaLeft = Math.max(0, mediaRect.left - hostRect.left);
    const mediaTop = Math.max(0, mediaRect.top - hostRect.top);
    const mediaWidth = Math.min(hostRect.width, mediaRect.width);
    const card = nav.querySelector('.g21-product-navigation__link');
    const cardWidth = card?.getBoundingClientRect().width || 92;
    const gap = 14;
    const rightSpace = hostRect.width - mediaLeft - mediaWidth;
    const sideLayoutFits = window.innerWidth >= 1024 &&
      mediaLeft >= cardWidth + gap + 8 &&
      rightSpace >= cardWidth + gap + 8;

    host.style.setProperty('--g21-navigation-media-left', `${mediaLeft}px`);
    host.style.setProperty('--g21-navigation-media-top', `${mediaTop}px`);
    host.style.setProperty('--g21-navigation-media-width', `${mediaWidth}px`);
    host.dataset.g21NavigationLayout = sideLayoutFits ? 'sides' : 'stacked';
  };

  const scheduleNavigationLayout = (host) => {
    if (!host?.isConnected) return;
    const previousFrame = navigationLayoutFrames.get(host);
    if (previousFrame) cancelAnimationFrame(previousFrame);
    const frame = requestAnimationFrame(() => {
      navigationLayoutFrames.delete(host);
      updateNavigationLayout(host);
    });
    navigationLayoutFrames.set(host, frame);
  };

  const installNavigationLayout = (scope = document) => {
    const hosts = [];
    if (scope instanceof Element && scope.matches(NAVIGATION_HOST_SELECTOR)) hosts.push(scope);
    scope.querySelectorAll?.(NAVIGATION_HOST_SELECTOR).forEach((host) => hosts.push(host));

    hosts.forEach((host) => {
      if (host.dataset.g21LayoutInitialized === 'true') {
        scheduleNavigationLayout(host);
        return;
      }
      host.dataset.g21LayoutInitialized = 'true';
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => scheduleNavigationLayout(host));
        observer.observe(host);
        const gallery = host.querySelector(':scope > media-gallery');
        if (gallery) observer.observe(gallery);
        navigationLayoutObservers.set(host, observer);
      }
      scheduleNavigationLayout(host);
    });
  };

  const initializeNavigation = async (scope = document) => {
    const nav = scope.querySelector?.('[data-g21-product-navigation]');
    if (!nav || nav.dataset.g21Initialized === 'true') return;
    installNavigationLayout(nav.closest(NAVIGATION_HOST_SELECTOR) || scope);
    nav.dataset.g21Initialized = 'true';
    installClickGuard(nav);

    const data = readData(scope);
    const collectionHandle = resolveCollection(scope, data);
    const serverLinkCount = activateServerLinks(nav, collectionHandle);
    if (serverLinkCount === 2) nav.hidden = false;

    try {
      const expectedCount = Math.max(0, Number(data.productCount) || 0);
      const products = orderedProducts(await fetchProductsOnce(collectionHandle, expectedCount));
      if (products.length < 2) throw new Error('Collection has fewer than two products');
      nav.dataset.g21ProductCount = String(products.length);
      const currentHandle = productHandleFromPath();
      let currentIndex = products.findIndex((product) => product.handle === currentHandle);
      if (currentIndex < 0) {
        const currentCode = codeParts({
          handle: currentHandle,
          title: document.querySelector('h1')?.textContent || document.title
        });
        if (currentCode) {
          currentIndex = products.findIndex((product) => {
            const candidateCode = codeParts(product);
            return candidateCode?.number === currentCode.number &&
              candidateCode?.suffix === currentCode.suffix;
          });
        }
      }
      if (currentIndex < 0) throw new Error('Current product is absent from collection');

      const previous = products[(currentIndex - 1 + products.length) % products.length];
      const next = products[(currentIndex + 1) % products.length];
      const previousReady = setLink(
        nav.querySelector('[data-g21-product-previous]'),
        previous,
        collectionHandle
      );
      const nextReady = setLink(
        nav.querySelector('[data-g21-product-next]'),
        next,
        collectionHandle
      );
      nav.hidden = !(previousReady && nextReady);
    } catch (_) {
      nav.hidden = serverLinkCount === 0 ? !useSelfFallback(nav, collectionHandle) : false;
    }
  };

  const smoothstep = (value) => {
    const t = Math.min(1, Math.max(0, value));
    return t * t * (3 - 2 * t);
  };

  const mix = (start, end, amount) => start + (end - start) * amount;

  const rateForProgress = (progress) => {
    if (progress <= 0.06) return 1;
    if (progress < 0.12) return mix(1, 0.65, smoothstep((progress - 0.06) / 0.06));
    if (progress <= 0.32) return 0.65;
    if (progress < 0.39) return mix(0.65, 1, smoothstep((progress - 0.32) / 0.07));
    if (progress <= 0.92) return 1;
    return mix(1, 0.8, smoothstep((progress - 0.92) / 0.08));
  };

  const videoControllers = new WeakMap();
  const activeVideoControllers = new Set();

  const isPrimaryGalleryVideo = (video) => {
    if (!(video instanceof HTMLVideoElement) || !video.isConnected) return false;
    if (video.closest('zoom-dialog, dialog, [data-g21-media-fallback]')) return false;

    const gallery = video.closest('media-gallery');
    if (!gallery) return false;

    if (gallery.dataset.presentation === 'grid') {
      return gallery.querySelector('.media-gallery__grid .product-media video') === video;
    }

    const slide = video.closest('slideshow-slide');
    return Boolean(slide && slide.getAttribute('aria-hidden') === 'false');
  };

  const createVideoController = (video) => {
    if (!isPrimaryGalleryVideo(video)) return null;
    const existing = videoControllers.get(video);
    if (existing) return existing;

    const abortController = new AbortController();
    const signal = abortController.signal;
    const controller = {
      video,
      frameId: 0,
      lastTime: 0,
      failed: false,
      visible: true
    };

    const setSafeRate = (target, immediate = false) => {
      if (controller.failed) return;
      try {
        const current = Number.isFinite(video.playbackRate) ? video.playbackRate : 1;
        const next = immediate ? target : current + (target - current) * 0.24;
        if (Math.abs(current - next) > 0.001) video.playbackRate = Math.min(1, Math.max(0.65, next));
      } catch (_) {
        controller.failed = true;
        try {
          video.playbackRate = 1;
        } catch (_) {}
      }
    };

    const updateRate = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      const restarted = video.currentTime + 0.25 < controller.lastTime;
      const progress = Math.min(1, Math.max(0, video.currentTime / video.duration));
      setSafeRate(restarted || progress < 0.005 ? 1 : rateForProgress(progress), restarted);
      controller.lastTime = video.currentTime;
    };

    const stop = () => {
      if (!controller.frameId) return;
      cancelAnimationFrame(controller.frameId);
      controller.frameId = 0;
    };

    const tick = () => {
      controller.frameId = 0;
      if (!video.isConnected) {
        controller.destroy();
        return;
      }
      updateRate();
      if (!video.paused && !video.ended) controller.frameId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (controller.failed || controller.frameId || video.paused || video.ended) return;
      controller.frameId = requestAnimationFrame(tick);
    };

    const autoplay = async () => {
      if (!controller.visible || !video.paused) return;
      video.playsInline = true;
      try {
        await video.play();
      } catch (_) {
        try {
          video.muted = true;
          await video.play();
        } catch (_) {}
      }
    };

    controller.setVisible = (visible) => {
      controller.visible = visible;
      if (visible) {
        autoplay();
      } else {
        video.pause();
      }
    };

    controller.destroy = () => {
      stop();
      abortController.abort();
      activeVideoControllers.delete(controller);
      try {
        video.playbackRate = 1;
      } catch (_) {}
    };

    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.defaultPlaybackRate = 1;
    video.addEventListener('play', start, { signal });
    video.addEventListener('playing', start, { signal });
    video.addEventListener('pause', stop, { signal });
    video.addEventListener('ended', () => {
      stop();
      controller.lastTime = 0;
      setSafeRate(1, true);
    }, { signal });
    video.addEventListener('loadedmetadata', () => {
      controller.lastTime = 0;
      setSafeRate(1, true);
      autoplay();
    }, { signal });
    video.addEventListener('seeking', updateRate, { signal });
    video.addEventListener('seeked', updateRate, { signal });

    videoControllers.set(video, controller);
    activeVideoControllers.add(controller);
    updateRate();
    if (!video.paused) start();
    return controller;
  };

  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          createVideoController(entry.target)?.setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.25);
        });
      }, { threshold: [0, 0.25] })
    : null;

  const initializeVideos = (scope = document) => {
    const videos = [];
    if (scope instanceof HTMLVideoElement && scope.matches(VIDEO_SELECTOR)) videos.push(scope);
    scope.querySelectorAll?.(VIDEO_SELECTOR).forEach((video) => videos.push(video));
    videos.forEach((video) => {
      if (!isPrimaryGalleryVideo(video)) return;
      const controller = createVideoController(video);
      if (videoObserver) {
        videoObserver.observe(video);
      } else {
        controller?.setVisible(true);
      }
    });
  };

  let videoFocusRequest = 0;
  let videoFocusTimeouts = [];

  const clearVideoFocusTimeouts = () => {
    videoFocusTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    videoFocusTimeouts = [];
  };

  const setVideoFocusMode = (active) => {
    if (!active) clearVideoFocusTimeouts();
    document.documentElement.classList.toggle('g21-video-focus-active', active);
  };

  const forceVideoScrollPosition = (slide, requestId, behavior) => {
    if (
      requestId !== videoFocusRequest ||
      !slide?.isConnected ||
      slide.getAttribute('aria-hidden') !== 'false'
    ) return;

    const rect = slide.getBoundingClientRect();
    const viewportHeight = Number(window.visualViewport?.height) || window.innerHeight;
    const topCrop = Math.round(Math.max(0, viewportHeight) * 0.05556);
    const top = Math.max(0, window.scrollY + rect.top + topCrop);

    try {
      window.scrollTo({ top, behavior });
    } catch (_) {
      try {
        window.scrollTo(0, top);
      } catch (_) {
        slide.scrollIntoView({ block: 'start', behavior });
      }
    }
  };

  const scrollVideoIntoFocus = (slide, requestId) => {
    clearVideoFocusTimeouts();
    setVideoFocusMode(true);
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    forceVideoScrollPosition(slide, requestId, prefersReducedMotion ? 'auto' : 'smooth');

    [120, 320, 700].forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        videoFocusTimeouts = videoFocusTimeouts.filter((id) => id !== timeoutId);
        forceVideoScrollPosition(slide, requestId, 'auto');
      }, delay);
      videoFocusTimeouts.push(timeoutId);
    });
  };

  const focusSelectedVideo = async (gallery, requestId, attempt = 0) => {
    if (requestId !== videoFocusRequest || !gallery?.isConnected) return;
    const activeSlide = gallery.querySelector('slideshow-slide[aria-hidden="false"]');
    const videoMedia = activeSlide?.querySelector(
      '[data-g21-product-media-video="true"], .product-media-container--video, .product-media-container--external_video'
    );

    if (!activeSlide || !videoMedia) {
      if (attempt < 4) {
        window.setTimeout(() => focusSelectedVideo(gallery, requestId, attempt + 1), 70);
      } else {
        setVideoFocusMode(false);
      }
      return;
    }

    const video = activeSlide.querySelector('video');
    if (video) {
      video.loop = true;
      video.playsInline = true;
      try {
        await video.play();
      } catch (_) {
        try {
          video.muted = true;
          await video.play();
        } catch (_) {
          return;
        }
      }
    }

    if (requestId !== videoFocusRequest) return;
    requestAnimationFrame(() => {
      if (requestId === videoFocusRequest) scrollVideoIntoFocus(activeSlide, requestId);
    });
  };

  const handleVideoFocusInteraction = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const gallery = target?.closest('media-gallery');
    if (!gallery) return;
    scheduleNavigationLayout(gallery.closest(NAVIGATION_HOST_SELECTOR));

    const control = target.closest(
      '.slideshow-control, [data-g21-product-video="true"], .product-media-container--video, .product-media-container--external_video'
    );
    if (!control) return;

    const requestId = ++videoFocusRequest;
    if (
      control.matches('.slideshow-controls__thumbnail') &&
      !control.querySelector('.slideshow-controls__thumbnail-badge')
    ) {
      setVideoFocusMode(false);
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusSelectedVideo(gallery, requestId));
    });
  };

  const start = () => {
    installNavigationLayout(document);
    initializeNavigation(document);
    initializeVideos(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('loadedmetadata', (event) => {
    initializeVideos(event.target);
    scheduleNavigationLayout(event.target.closest?.(NAVIGATION_HOST_SELECTOR));
  }, true);
  document.addEventListener('play', (event) => initializeVideos(event.target), true);
  document.addEventListener('click', handleVideoFocusInteraction);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setVideoFocusMode(false);
  });
  document.addEventListener('shopify:section:load', (event) => {
    initializeNavigation(event.target);
    initializeVideos(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(NAVIGATION_HOST_SELECTOR).forEach((host) => {
      navigationLayoutObservers.get(host)?.disconnect();
      navigationLayoutObservers.delete(host);
      const frame = navigationLayoutFrames.get(host);
      if (frame) cancelAnimationFrame(frame);
      navigationLayoutFrames.delete(host);
    });
    event.target.querySelectorAll?.(VIDEO_SELECTOR).forEach((video) => {
      videoObserver?.unobserve(video);
      videoControllers.get(video)?.destroy();
    });
  });
  window.addEventListener('pagehide', () => {
    setVideoFocusMode(false);
    navigationLayoutObservers.forEach((observer) => observer.disconnect());
    navigationLayoutObservers.clear();
    activeVideoControllers.forEach((controller) => controller.destroy());
    videoObserver?.disconnect();
  }, { once: true });

  window.G21CollectionProductNavigation = Object.freeze({
    installed: true,
    disabled: false,
    refresh: start
  });
  window.G21ProductVideoRamp = Object.freeze({
    installed: true,
    disabled: false,
    refresh: () => initializeVideos(document)
  });
})();
